import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "option"];
import PropTypes from 'prop-types';
import { useContext } from 'react';
import I18n from 'I18n';
import getIn from 'immutable-less/transmute/getIn';
import { ASSIGNABLE_USER, LEGACIED_ASSIGNABLE_USER } from 'automation-ui-action-features/users-and-teams/AssignableUserResolver';
import { WORKFLOWS_ASSIGNMENT_ACCESS_LEGACY } from 'automation-ui-action-features/constants/Scopes';
import { useHasScope } from 'automation-ui-core/hooks/authHooks';
import { paidUserSettingsUrl } from 'automation-ui-references/routing/hubspotUrls';
import { SupportLinkKeys } from 'automation-ui-usage-tracking/SupportLinks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-usage-tracking/trackingUtils';
import UsageTrackerContext from 'automation-ui-usage-tracking/UsageTrackerContext';
import DocButton from 'automation-ui-usage-tracking/components/DocButton';
// @ts-expect-error untyped module customer-data-reference-ui-components
import ConnectedAPIDropdown from 'customer-data-reference-ui-components/connector/ConnectedAPIDropdown';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { jsx as _jsx } from "react/jsx-runtime";
const getConnectedAPIDropdownValue = (value, isMultiSelect) => {
  if (isMultiSelect) {
    return value ? value.map(userId => `${userId}`) : [];
  } else {
    return value ? `${value}` : '';
  }
};
const allowLinksToBeClickedInTooltips = event => event.stopPropagation();
export const handleClickTrackedLink = usageTracker => {
  trackEvent(usageTracker, Trackables.VIEW_PAID_USER_SETTINGS);
  window.open(paidUserSettingsUrl());
};
export const getDisabledOptionTooltipTitle = ({
  isAssignable,
  isDeactivated,
  usageTracker
}) => {
  if (isDeactivated) {
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "automation-ui-action-features.users-and-teams.assignable-user-select.deactivated-user.tooltip_jsx",
      elements: {
        Link: ({
          children
        }) => /*#__PURE__*/_jsx(DocButton, {
          supportKey: SupportLinkKeys.DEACTIVATED_USER,
          use: "link-on-dark",
          children: children
        })
      }
    });
  } else if (!isAssignable) {
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "automation-ui-action-features.users-and-teams.assignable-user-select.non-assignable-user.tooltip_jsx",
      elements: {
        Link: ({
          children
        }) => /*#__PURE__*/_jsx(UILink, {
          external: true,
          onClick: () => handleClickTrackedLink(usageTracker),
          use: "on-dark",
          children: children
        })
      }
    });
  }
  return null;
};
const ItemComponent = _ref => {
  let {
      children,
      option
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    usageTracker
  } = useContext(UsageTrackerContext);
  const isAssignable = getIn(['record', 'referencedObject', 'isAssignable'], option);
  const isDeactivated = getIn(['record', 'referencedObject', 'isDeactivated'], option);
  const tooltipTitle = getDisabledOptionTooltipTitle({
    isAssignable,
    isDeactivated,
    usageTracker: usageTracker
  });
  return /*#__PURE__*/_jsx("span", Object.assign({}, restProps, {
    children: /*#__PURE__*/_jsx(UITooltip, {
      disabled: !tooltipTitle
      // @ts-expect-error onMouseDown does not exist on props
      ,
      onMouseDown: allowLinksToBeClickedInTooltips,
      title: tooltipTitle,
      children: children
    })
  }));
};
const propTypes = {
  'data-test-id': PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  multi: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  overrideAssignableAccess: PropTypes.bool,
  placeholder: PropTypes.node,
  readOnly: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string])
};
const AssignableUserSelect = ({
  'data-test-id': dataTestId,
  disabled,
  error,
  multi,
  onChange,
  overrideAssignableAccess,
  placeholder,
  readOnly,
  value
}) => {
  const legaciedAssignableAccess = useHasScope(WORKFLOWS_ASSIGNMENT_ACCESS_LEGACY);
  const hasFullOwnerAssignmentAccess = legaciedAssignableAccess || overrideAssignableAccess;
  return /*#__PURE__*/_jsx(ConnectedAPIDropdown, {
    "data-test-id": dataTestId,
    disabled: disabled,
    error: error,
    itemComponent: ItemComponent,
    menuWidth: "auto",
    multi: multi,
    onChange: onChange,
    placeholder: placeholder || I18n.text('automation-ui-action-features.users-and-teams.assignable-user-select.placeholder'),
    readOnly: readOnly,
    referenceObjectType: hasFullOwnerAssignmentAccess ? LEGACIED_ASSIGNABLE_USER : ASSIGNABLE_USER,
    value: getConnectedAPIDropdownValue(value, multi)
  });
};
AssignableUserSelect.propTypes = propTypes;
export default AssignableUserSelect;